/** Stock surface body for the Inventory desk (search, tabs, posting). */
import { InlineHelp } from "@/components/common/InlineHelp";
import { SectionCard } from "@/components/common/SectionCard";
import type {
  MfgMaterial,
  MfgRecipeInventoryRow,
  MfgRecipeSummary,
} from "@/lib/manufacturing/types";
import { ManufacturingInventoryChangelogBody } from "./ManufacturingInventoryChangelogBody";
import { ManufacturingInventoryMaterialsTabBody } from "./ManufacturingInventoryMaterialsTabBody";
import { materialReplaceAppliedToast, materialUseCreatedToast } from "./materialReplaceAppliedToast";
import {
  ManufacturingInventorySearchCard,
  type ActiveFilter,
  type MsdsStatusFilter,
  type PriceFilter,
  type QtyFilter,
  type RecipeUsageFilter,
} from "./ManufacturingInventorySearchCard";
import { ManufacturingInventoryRecipesTabBody } from "./ManufacturingInventoryRecipesTabBody";
import { ManufacturingReceiptsAttentionBanner } from "./ManufacturingReceiptsAttentionBanner";
import { ManufacturingReceiveLinesBody } from "./ManufacturingReceiveLinesBody";
import { ManufacturingTabbedSection } from "./ManufacturingTabbedSection";
import { RecipeCountBody } from "./RecipeCountBody";
import {
  INVENTORY_VIEW_TABS,
  POSTING_TABS,
  type InventoryViewTab,
  type PostingTab,
} from "./manufacturingInventoryTabs";
import type { useManufacturingInventoryCleanup } from "./useManufacturingInventoryCleanup";
import type { useManufacturingInventoryDeletions } from "./useManufacturingInventoryDeletions";
import type { useManufacturingReceiveLines } from "./useManufacturingReceiveLines";
import type { useManufacturingReceiptsAttention } from "./useManufacturingReceiptsAttention";

type Cleanup = ReturnType<typeof useManufacturingInventoryCleanup>;
type Deletions = ReturnType<typeof useManufacturingInventoryDeletions>;
type Receive = ReturnType<typeof useManufacturingReceiveLines>;
type ReceiptsAttention = ReturnType<typeof useManufacturingReceiptsAttention>;

export function ManufacturingInventoryDeskStockBody(props: {
  search: string;
  onSearchChange: (v: string) => void;
  category: string;
  onCategoryChange: (v: string) => void;
  categories: string[];
  activeFilter: ActiveFilter;
  onActiveFilterChange: (v: ActiveFilter) => void;
  msdsStatusFilter: MsdsStatusFilter;
  onMsdsStatusFilterChange: (v: MsdsStatusFilter) => void;
  recipeUsageFilter: RecipeUsageFilter;
  onRecipeUsageFilterChange: (v: RecipeUsageFilter) => void;
  qtyFilter: QtyFilter;
  onQtyFilterChange: (v: QtyFilter) => void;
  priceFilter: PriceFilter;
  onPriceFilterChange: (v: PriceFilter) => void;
  filtered: MfgMaterial[];
  materials: MfgMaterial[];
  inventoryValue: number;
  costingLastUpdatedAt: string | null;
  busy: boolean;
  gateEnabled: boolean;
  inventoryTab: InventoryViewTab;
  onInventoryTabChange: (t: InventoryViewTab) => void;
  postingTab: PostingTab;
  onPostingTabChange: (t: PostingTab) => void;
  duplicatesOpen: boolean;
  onToggleDuplicates: () => void;
  onCloseDuplicates: () => void;
  recipeDupsOpen: boolean;
  onToggleRecipeDups: () => void;
  onCloseRecipeDups: () => void;
  onCreate: () => void;
  onEdit: (m: MfgMaterial) => void;
  onOpenTransactions: (m: MfgMaterial) => void;
  onOpenSds: (m: MfgMaterial) => void;
  onBulkImport: () => void;
  onTemplate: () => void;
  onInventoryCount?: () => void;
  onSdsBook: () => void;
  cleanup: Cleanup;
  deletions: Deletions;
  canPermanentDeleteMaterial: boolean;
  canDeactivateModel: boolean;
  recipes: MfgRecipeSummary[];
  recipesLoading: boolean;
  recipeInventoryRows: MfgRecipeInventoryRow[];
  recipeInventoryTotalValue: number;
  recipeInventoryLoading: boolean;
  recipeInventoryError: string | null;
  onRenameRecipe: Cleanup["renameRecipe"];
  onDeactivateRecipe: Cleanup["deactivateRecipe"];
  receive: Receive;
  receiptsAttention: ReceiptsAttention;
  onOpenPurchasing?: () => void;
  onRefreshMaterials: () => void;
  onRefreshRecipeInventory: () => void;
  setImportToast: (msg: string) => void;
  setRecipeToast: (msg: string) => void;
}) {
  const {
    cleanup,
    deletions,
    materials,
    filtered,
    categories,
    busy,
    gateEnabled,
  } = props;

  return (
    <>
      <ManufacturingReceiptsAttentionBanner
        count={props.receiptsAttention.count}
        items={props.receiptsAttention.items}
        onSwitchToReceipts={() => props.onOpenPurchasing?.()}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <ManufacturingInventorySearchCard
          search={props.search}
          onSearchChange={props.onSearchChange}
          category={props.category}
          onCategoryChange={props.onCategoryChange}
          categories={categories}
          activeFilter={props.activeFilter}
          onActiveFilterChange={props.onActiveFilterChange}
          msdsStatusFilter={props.msdsStatusFilter}
          onMsdsStatusFilterChange={props.onMsdsStatusFilterChange}
          recipeUsageFilter={props.recipeUsageFilter}
          onRecipeUsageFilterChange={props.onRecipeUsageFilterChange}
          qtyFilter={props.qtyFilter}
          onQtyFilterChange={props.onQtyFilterChange}
          priceFilter={props.priceFilter}
          onPriceFilterChange={props.onPriceFilterChange}
        />
        <SectionCard
          title="Snapshot"
          className="md:col-span-2 h-full order-1 md:order-none"
          rightSlot={
            <InlineHelp label="Costing snapshot timestamp" glyph="!" placement="left">
              <div className="font-medium text-gray-800 dark:text-zinc-100">Costing snapshot</div>
              <div className="mt-0.5">
                {props.costingLastUpdatedAt
                  ? new Date(props.costingLastUpdatedAt).toLocaleString()
                  : "Not posted yet"}
              </div>
            </InlineHelp>
          }
        >
          <div className="grid min-w-0 grid-cols-2 gap-3">
            <div className="min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
              <div className="text-xs text-gray-600 dark:text-zinc-300">Total items</div>
              <div className="mt-1 text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                {filtered.length}
              </div>
            </div>
            <div className="min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
              <div className="text-xs text-gray-600 dark:text-zinc-300">Inventory value</div>
              <div className="mt-1 break-all text-lg font-bold tabular-nums leading-tight text-emerald-700 sm:text-2xl dark:text-emerald-300">
                {props.inventoryValue.toLocaleString(undefined, { style: "currency", currency: "USD" })}
              </div>
            </div>
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ManufacturingTabbedSection<InventoryViewTab>
          tabs={INVENTORY_VIEW_TABS}
          activeTab={props.inventoryTab}
          onTabChange={props.onInventoryTabChange}
        >
          {props.inventoryTab === "materials" ? (
            <ManufacturingInventoryMaterialsTabBody
              allMaterials={materials}
              filteredMaterials={filtered}
              existingCategories={categories.filter((c) => c !== "all")}
              gateEnabled={gateEnabled}
              busy={busy}
              duplicatesOpen={props.duplicatesOpen}
              onToggleDuplicates={props.onToggleDuplicates}
              onCloseDuplicates={props.onCloseDuplicates}
              onCreate={props.onCreate}
              onEdit={props.onEdit}
              onOpenTransactions={props.onOpenTransactions}
              onOpenSds={props.onOpenSds}
              onBulkImport={props.onBulkImport}
              onTemplate={props.onTemplate}
              onInventoryCount={() => props.onInventoryCount?.()}
              onSdsBook={props.onSdsBook}
              onDeactivateMaterial={cleanup.deactivateMaterial}
              selectedMaterialIds={cleanup.selectedMaterialIds}
              onToggleSelection={cleanup.toggleMaterialSelection}
              onToggleSelectAllOnPage={cleanup.toggleMaterialSelectAllOnPage}
              onClearSelection={cleanup.clearMaterialSelection}
              onBulkApplied={({ updated, failed }) => {
                cleanup.clearMaterialSelection();
                props.onRefreshMaterials();
                props.setImportToast(
                  failed > 0
                    ? `Reassigned ${updated}; ${failed} failed`
                    : `Reassigned ${updated} material${updated === 1 ? "" : "s"}`
                );
              }}
              onMaterialReplaceApplied={(summary) => {
                cleanup.clearMaterialSelection();
                props.onRefreshMaterials();
                props.setImportToast(materialReplaceAppliedToast(summary));
              }}
              onMaterialUseCreated={(summary) =>
                props.setImportToast(materialUseCreatedToast(summary))
              }
              onRequestDeleteMaterial={deletions.requestSingleDelete}
              onRequestRestoreMaterial={deletions.requestSingleRestore}
              onRequestPermanentDeleteMaterial={
                props.canPermanentDeleteMaterial
                  ? deletions.requestPermanentDelete
                  : undefined
              }
              onRequestBulkDelete={deletions.requestBulkDelete}
              onRequestBulkRestore={deletions.requestBulkRestore}
              canMergeDuplicates={props.canPermanentDeleteMaterial}
              onMaterialMerged={(s) => {
                cleanup.clearMaterialSelection();
                props.onRefreshMaterials();
                props.setImportToast(`Merged ${s.fromName} into ${s.toName}`);
              }}
            />
          ) : props.inventoryTab === "recipes" ? (
            <ManufacturingInventoryRecipesTabBody
              recipes={props.recipes}
              inventoryRows={props.recipeInventoryRows}
              totalInventoryValue={props.recipeInventoryTotalValue}
              loading={props.recipeInventoryLoading}
              error={props.recipeInventoryError}
              gateEnabled={gateEnabled}
              busy={busy}
              duplicatesOpen={props.recipeDupsOpen}
              onToggleDuplicates={props.onToggleRecipeDups}
              onCloseDuplicates={props.onCloseRecipeDups}
              onRenameRecipe={props.onRenameRecipe}
              onDeactivateRecipe={props.onDeactivateRecipe}
              canDeactivateRecipe={props.canDeactivateModel}
            />
          ) : (
            <ManufacturingInventoryChangelogBody enabled={gateEnabled} />
          )}
        </ManufacturingTabbedSection>

        <ManufacturingTabbedSection<PostingTab>
          tabs={POSTING_TABS}
          activeTab={props.postingTab}
          onTabChange={props.onPostingTabChange}
        >
          {props.postingTab === "receive" ? (
            <ManufacturingReceiveLinesBody receive={props.receive} materials={materials} />
          ) : (
            <RecipeCountBody
              recipes={props.recipes}
              recipesLoading={props.recipesLoading}
              onPosted={(msg) => {
                props.setRecipeToast(msg);
                props.onRefreshRecipeInventory();
              }}
            />
          )}
        </ManufacturingTabbedSection>
      </div>
    </>
  );
}
